import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { computed, ref } from 'vue';

import { useMediaPopupStore } from '@/stores/media-popup';

/**
 * An item from the media-popup store's heterogeneous `contextList` (media, pins,
 * deliverables, etc.). Only the fields this composable reads are declared; the
 * index signature covers the dynamic `[source]` lookup for a per-platform timestamp.
 */
interface MediaContextListItem {
  id?: number | string;
  sourceId?: number | string;
  source?: string;
  [key: string]: unknown;
}

interface OpenMediaArgs {
  id: number | string;
  brandId?: number;
  brandLabel?: string;
  source?: string;
  patchJson?: Record<string, unknown> | null;
  /** The page section the post was opened from, reported on `Media Pop-up Opened`. */
  section?: string;
}

/** Props passed to the media drawer for a single post. */
interface MediaDrawerProps {
  id?: number | string;
  sourceId?: number | string;
  patchJson: Record<string, unknown>;
  brandLabel?: string;
  brandId?: number;
  source?: string;
}

/** Minimal shape of the media object handed to `updatePropsListIndex`. */
interface CurrentMedia {
  id?: number | string;
  postSourceId?: number | string;
}

const propsListIndex = ref<number | null>(null);
const context = ref<Partial<OpenMediaArgs>>({});

export function useMediaDrawer() {
  const mediaPopupStore = useMediaPopupStore();
  const { launchDrawer } = useDrawer();

  const propsList = computed<MediaDrawerProps[]>(() =>
    ((mediaPopupStore.contextList ?? []) as MediaContextListItem[])?.map((contextListItem) => {
      const id = contextListItem.id || contextListItem.sourceId;
      const source = contextListItem.source;

      const patchJson: Record<string, unknown> = { ...context.value.patchJson, media_id: id };
      const sourceItem = source
        ? (contextListItem[source] as { timestamp?: unknown } | undefined)
        : undefined;
      if (sourceItem?.timestamp) {
        patchJson.timestamp = sourceItem.timestamp;
      }

      return {
        id,
        patchJson,

        brandLabel: context.value.brandLabel,
        brandId: context.value.brandId,
        source: context.value.source,
      };
    }),
  );

  /**
   * Launch the global media drawer for a post.
   *
   * Brand context (brandId / brandLabel) falls back to the active brand inside
   * MediaPopup's data fetch, so callers can omit it when targeting the current
   * brand.
   *
   * When `source` is omitted, MediaPopup routes by library id
   * (`/brands/:bid/media/:id?allow_index=true`). When `source` is provided,
   * it routes by platform id (`/media/source/:id?source_type=...`) and uses
   * `patchJson` (with `instagram_name` / `tiktok_name` + optional `timestamp`)
   * to index the post on demand.
   */
  async function openMedia({
    id,
    brandId,
    brandLabel,
    source,
    patchJson: incomingPatchJson,
    section,
  }: OpenMediaArgs) {
    const patchJson = { ...incomingPatchJson, media_id: id };

    context.value = { brandId, brandLabel, source, patchJson, section };

    void launchDrawer({
      name: 'media',
      props: {
        id,
        brandId,
        brandLabel,
        source,
        patchJson,
      },
      propsList: propsList.value,
    });
  }

  function updatePropsListIndex(media: CurrentMedia | null | undefined) {
    if (!media || !propsList.value) {
      propsListIndex.value = null;
      return;
    }

    propsListIndex.value = propsList.value.findIndex((item) => {
      if (item.id) {
        return item.id === media.id || item.id === media.postSourceId;
      }
      return item.sourceId === media.postSourceId;
    });
  }

  const hasPreviousMedia = computed(() => {
    if (propsList.value?.length > 0) {
      return typeof propsListIndex.value === 'number' && propsListIndex.value > 0;
    }
    return false;
  });

  const hasNextMedia = computed(() => {
    if (propsList.value?.length > 0) {
      return (
        typeof propsListIndex.value === 'number' &&
        propsListIndex.value < propsList.value.length - 1
      );
    }
    return false;
  });

  function loadPreviousMedia() {
    if (!hasPreviousMedia.value || propsListIndex.value === null) {
      return null;
    }

    propsListIndex.value -= 1;
    const previousProps = propsList.value[propsListIndex.value];

    void openMedia({ ...previousProps, section: context.value.section } as OpenMediaArgs);

    return previousProps;
  }

  function loadNextMedia() {
    if (!hasNextMedia.value || propsListIndex.value === null) {
      return null;
    }

    propsListIndex.value += 1;
    const nextProps = propsList.value[propsListIndex.value];

    void openMedia({ ...nextProps, section: context.value.section } as OpenMediaArgs);

    return nextProps;
  }

  const openedFromSection = computed(() => context.value.section);

  return {
    openMedia,
    openedFromSection,
    hasPreviousMedia,
    hasNextMedia,
    loadPreviousMedia,
    loadNextMedia,
    updatePropsListIndex,
  };
}
